import {Component, OnInit, Input, Output, EventEmitter, OnChanges} from '@angular/core';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import { UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {TypesService, ProductService} from '../../../services';
import {DataHandlerService} from '../../../services/data-handler.service';
import { ValidationPartialComponent } from '../partials/validation-partial.component';
import { MatOptionModule } from '@angular/material/core';
import { NgFor } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';

@Component({
    selector: 'app-loan-details',
    templateUrl: './loan-details.component.html',
    styles: [],
    standalone: true,
    imports: [MatCardModule, MatCardModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatFormFieldModule, MatSelectModule, MatSelectModule, NgFor, MatOptionModule, MatOptionModule, ValidationPartialComponent]
})

export class LoanDetailsComponent extends WizardWidgetComponent implements OnInit, OnChanges {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public brokerDetails: any;
  @Input() public selectedLoanDescription: any;
  @Input() public loanPurposeType: any;
  @Input() public isPersonalLoan: any;
  @Input() public isSMSF: any;
  @Output() public loanProductTypeChange = new EventEmitter<any>();
  @Output() public loanPurposeChange = new EventEmitter<any>();
  @Output() public loanInsuranceList = new EventEmitter<any[]>();

  public productTypes: any;
  public productVariations = [];
  public loanPurposes = [];
  public insurancePurposes = [];
  public enableProductList = false;

  private _broker: any;
  private _collateral: any;
  private _loanPurpose: any;
  private _productType: any;
  private _loanPurposesTypeSlug: any;
  private _loanPurposesCategoryTypeSlug: any;
  private _productCategoryId: any;
  private _loanPurposeGroupIds = [];
  private _insurancePurposeGroupId = [];
  private _loanPurposes = [];
  private _securityType = 'new';

  constructor(public dataHandlerService: DataHandlerService,
              private typesService: TypesService,
              private productService: ProductService) {
    super(dataHandlerService);
  }

  ngOnInit() {
    super.ngOnInit();

    this.setGlobals();
    this.initSubscriptions();
    this.populatePurposes();
  }

  ngOnChanges(changes) {
    // capture broker details
    if (changes['brokerDetails']) {
      this._broker = changes['brokerDetails']['currentValue'];
    }

    // capture car selected / collateral
    if (changes['selectedLoanDescription']) {
      this._collateral = changes['selectedLoanDescription']['currentValue'];
    }

    // get products
    this.getVxProducts();

    // capture values after refresh
    if (changes['formGroup'] && changes['formGroup']['firstChange']) {

      if (changes['formGroup']['currentValue']['value']['purpose']) {
        this._loanPurpose = changes['formGroup']['currentValue']['value']['purpose'];
      }
      this.emitLoanDetailsChange('loanPurpose', changes['formGroup']['currentValue']['value']['purpose']);
    }
  }

  // Set all the necessary globals based on loanPurposeType
  public setGlobals() {
    switch (this.loanPurposeType) {
      case 'motor-vehicle-loan':
        this._productType = 'motor-vehicle';
        this._loanPurposesTypeSlug = 'car-loan-loan-purpose-types-vx';
        this._loanPurposesCategoryTypeSlug = 'loan-purposes-with-product-category';
        this._loanPurposeGroupIds = [1, 2];
        this._insurancePurposeGroupId = [5];
        if (this.isPersonalLoan) {
          this._productCategoryId = 2;
        } else {
          this._productCategoryId = 17;
        }
        break;

      default:
        throw new Error('Unknown loanPurposeType: ' + this.loanPurposeType);
    }
  }

  public initSubscriptions() {
    // handle changes in loan product type
    this.formGroup.get('product').valueChanges
      .subscribe(
        val => {
          console.debug('LoanDetailsComponent.product.valueChanges');
          this.processSelectedProduct(val);
        }
      );

    // handle changes in loan purpose
    this.formGroup.get('purpose').valueChanges
      .subscribe(
        val => {
          console.debug('LoanDetailsComponent.purpose.valueChanges');
          this._loanPurpose = val;
          this.setSecurityType(val);
          this.emitLoanDetailsChange('loanPurpose', val);
          this.getVxProducts();
        }
      );
  }

  public setSecurityType(purpose) {
    switch (purpose) {
      case 'purchase-new-business':
      case 'purchase-new-personal':
        this._securityType = 'new';
        break;

      case 'purchase-used-business':
      case 'purchase-used-personal':
        this._securityType = 'used-dealer';
        break;

      case 'refinance-personal':
        this._securityType = 'used-private';
        break;
    }
    this.formGroup.get('securityType').patchValue(this._securityType);
  }

  public emitLoanDetailsChange(key: string, value: any) {
    switch (key) {
      case 'loanProductType':
        this.loanProductTypeChange.emit(value);
        break;

      case 'loanPurpose':
        this.loanPurposeChange.emit(value);
        break;

      case 'loanInsurance':
        this.loanInsuranceList.emit(value);
        break;

      default:
        throw new Error('Unhandled emit key: ' + key);
    }
  }

  public populatePurposes() {
    // get vx purposes
    this.typesService.getTypesList(this._loanPurposesTypeSlug)
      .then(results => {
        // console.log('[purposes]', results);
        results.filter(res => {
          if (res.ext.Active) {
            if (this._loanPurposeGroupIds.includes(res.ext.LoanPurposeGroupID)) {
              this._loanPurposes.push(res);
            } else if (this._insurancePurposeGroupId.includes(res.ext.LoanPurposeGroupID)) {
              this.insurancePurposes.push(res);
            } else {
              console.log('[WARN]', 'Unused purpose:', res.id);
            }
          }
        });

        // get categories (old purposes)
        this.typesService.getTypesList(this._loanPurposesCategoryTypeSlug)
          .then(purposes => {
            purposes.filter(p => {
              if (p.ext.Active && p.ext.ProductCategoryID === this._productCategoryId) {
                p.slug = this.findPurposeById(p.id);
                // console.log('purpose:', p);
                this.loanPurposes.push(p);
              }
            });
          });
        this.emitLoanDetailsChange('loanInsurance', this.insurancePurposes);
      });
  }

  findPurposeById(id) {
    let slug = null;
    this._loanPurposes.forEach(p => {
      if (p.ext.ID === id) {
        slug = p.id;
      }
    });
    return slug;
  }

  public getVxProducts() {
    if (this._broker && this._collateral && this._loanPurpose) {
      this.productService.getMotorVehicleVariationsList(this._productType, this._broker.id, this._loanPurpose, this._securityType, this._collateral.year)
        .subscribe(results => {
          this.productVariations = results;
          if (this.productVariations.length === 1) {
            this.formGroup.get('product').setValue(this.productVariations[0].slug);
            if (this.productVariations[0].pricing && this.productVariations[0].pricing.length) {
              this.formGroup.get('productPricing').setValue(this.productVariations[0].pricing[0].slug);
            }
          }
          this.enableProductList = true;
        });
    }
  }

  public processSelectedProduct(slug) {
    this.productVariations.forEach(variation => {
      if (variation.slug === slug) {
        this.emitLoanDetailsChange('loanProductType', variation);
      }
    });
  }
}
